import { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { CallStatusType } from "../../../configs";
import { AudioConferenceCall } from "../../../models";
import { SendOutboundDtmfRequestModel } from "../../../models/api/Call";
import { formatUSPhoneNumber } from "../../../utils/phoneNumberFormatting";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  callSid?: string | null;
  selectedCall?: AudioConferenceCall | null;
  onSendOutboundDtmf: (value: SendOutboundDtmfRequestModel) => Promise<any>;
};

const keypadRows = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

function getCallStatusLabel(current?: CallStatusType, prev?: CallStatusType): string {
  if (!current) return "Waiting";

  switch (current) {
    case CallStatusType.OutboundCallInitiated:
      return "Initiated";
    case CallStatusType.OutboundCallRinging:
      return "Ringing";
    case CallStatusType.OutboundCallReceived:
      return "In-call";
    case CallStatusType.OutboundCallCompleted:
      if (prev === CallStatusType.OutboundCallReceived) return "Hung Up";
      if (prev === CallStatusType.OutboundCallNotAnswered) return "Not Answered";
      return "Completed";
    case CallStatusType.OutboundCallNotAnswered:
      return "Not Answered";
    case CallStatusType.OutboundCallNotReceived:
      return "Voice Mail";
    default:
      return "Waiting";
  }
}

export function ModalOutboundDtmfDial({
  show,
  handleShow,
  callSid,
  selectedCall,
  onSendOutboundDtmf,
}: Props) {
  const sendQueueRef = useRef(Promise.resolve());
  const [codeInput, setCodeInput] = useState("");
  const [legCallSidInput, setLegCallSidInput] = useState("");
  const [lastAutoPopulatedLegCallSid, setLastAutoPopulatedLegCallSid] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [lastSentDigits, setLastSentDigits] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");

  useEffect(() => {
    if (!show) {
      setCodeInput("");
      setLegCallSidInput("");
      setLastAutoPopulatedLegCallSid("");
      setIsSending(false);
      setLastSentDigits("");
      setFeedbackMessage("");
    }
  }, [show]);

  useEffect(() => {
    const nextLegCallSid = selectedCall?.callSid ?? "";

    if (!nextLegCallSid) {
      return;
    }

    if (!legCallSidInput || legCallSidInput === lastAutoPopulatedLegCallSid) {
      setLegCallSidInput(nextLegCallSid);
      setLastAutoPopulatedLegCallSid(nextLegCallSid);
    }
  }, [lastAutoPopulatedLegCallSid, legCallSidInput, selectedCall?.callSid]);

  const appendKey = (digit: string) => {
    setFeedbackMessage("");
    setCodeInput((prev) => prev + digit);

    if (!callSid) {
      setFeedbackMessage("Parent call SID is missing.");
      return;
    }

    if (!legCallSidInput) {
      setFeedbackMessage("legCallSid is required.");
      return;
    }

    const currentCallSid = callSid;
    const currentLegCallSid = legCallSidInput;

    sendQueueRef.current = sendQueueRef.current.then(async () => {
      setIsSending(true);
      setFeedbackMessage(`Sending ${digit}`);

      try {
        await onSendOutboundDtmf({
          callSid: currentCallSid,
          legCallSid: currentLegCallSid,
          digits: digit,
        });
        setLastSentDigits(digit);
        setFeedbackMessage(`Sent ${digit}`);
      } catch (error) {
        console.error(error);
        setFeedbackMessage(`Failed to send ${digit}`);
      } finally {
        setIsSending(false);
      }
    });
  };

  const callStatusLabel = getCallStatusLabel(selectedCall?.status?.current, selectedCall?.status?.prev);
  const targetNumberLabel =
    formatUSPhoneNumber(selectedCall?.calledNumber || "") ||
    selectedCall?.calledNumber ||
    "";

  return (
    <Modal show={show} onHide={() => handleShow(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Keypad</Modal.Title>
      </Modal.Header>

      <Modal.Body className="dialer-modal-body">
        <div className="dialer-modal">
          <div className="d-flex flex-column justify-content-center align-items-center dialer-content">
            <div className="w-100 mb-3" style={{ maxWidth: "320px" }}>
              <div className="d-flex align-items-end mb-2" style={{ gap: "8px" }}>
                <div style={{ flex: "1 1 0" }}>
                  <label style={{ fontSize: "11px", fontWeight: 600, marginBottom: "4px", display: "block" }}>
                    callSid
                  </label>
                  <input
                    className="form-control form-control-sm"
                    value={callSid ?? ""}
                    readOnly
                    style={{ height: "32px" }}
                  />
                </div>
                <div style={{ flex: "1 1 0" }}>
                  <label style={{ fontSize: "11px", fontWeight: 600, marginBottom: "4px", display: "block" }}>
                    Code
                  </label>
                  <input
                    className="form-control form-control-sm"
                    value={codeInput}
                    readOnly
                    onKeyDown={(event) => event.preventDefault()}
                    onPaste={(event) => event.preventDefault()}
                    placeholder="Code"
                    style={{ height: "32px" }}
                  />
                </div>
              </div>
              <div className="d-flex align-items-end" style={{ gap: "8px" }}>
                <div style={{ flex: "1 1 auto" }}>
                  <label style={{ fontSize: "11px", fontWeight: 600, marginBottom: "4px", display: "block" }}>
                    legCallSid
                  </label>
                  <input
                    className="form-control form-control-sm"
                    value={legCallSidInput}
                    onChange={(event) => {
                      setFeedbackMessage("");
                      setLegCallSidInput(event.target.value);
                    }}
                    placeholder="Editable leg call SID"
                    style={{ height: "32px" }}
                  />
                </div>
              </div>
            </div>

            <div className="d-flex flex-row">
              <div
                className="dialer-output shadow-sm"
                style={{
                  minWidth: codeInput.length > 25 ? codeInput.length * 10 + "px" : "250px",
                }}
              >
                <input
                  className="border form-control text-center"
                  value={codeInput}
                  readOnly
                  onKeyDown={(event) => event.preventDefault()}
                  onPaste={(event) => event.preventDefault()}
                  placeholder="Enter code"
                  style={{
                    minWidth: codeInput.length > 25 ? codeInput.length * 10 + "px" : "250px",
                  }}
                />
              </div>
            </div>

            <div className="key-row-wrapper mt-3">
              {keypadRows.map((row) => (
                <div className="key-row" key={row.join("-")}>
                  {row.map((digit) => (
                    <button
                      key={digit}
                      value={digit}
                      className="btn btn-light shadow-sm ripple"
                      type="button"
                      onClick={() => appendKey(digit)}
                    >
                      {digit}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            <div className="text-center mt-3" style={{ maxWidth: "260px", fontSize: "12px" }}>
              <div style={{ fontWeight: 600 }}>{targetNumberLabel || "Selected call"}</div>
              <div style={{ color: "#198754" }}>{callStatusLabel}</div>
              <div className="mt-2" style={{ color: "#6c757d" }}>
                Each keypad click sends that single digit immediately.
              </div>
              {!callSid && <div style={{ color: "#dc3545" }}>Parent call SID is missing.</div>}
              {!selectedCall && (
                <div style={{ color: "#fd7e14" }}>No active call was selected.</div>
              )}
              {!legCallSidInput && selectedCall && !selectedCall.callSid && (
                <div style={{ color: "#fd7e14" }}>Waiting for outbound leg SID.</div>
              )}
              {isSending && <div style={{ color: "#0d6efd" }}>Sending digits...</div>}
              {!isSending && feedbackMessage && <div>{feedbackMessage}</div>}
              {!isSending && lastSentDigits && <div>Last sent: {lastSentDigits}</div>}
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
